import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Typography,
  Divider,
  Tabs,
  Tab,
  Paper,
  Chip
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, Science as ScienceIcon } from '@mui/icons-material';
import { Locale, MeasurementUnit } from '../../../services/workout.service';
import { WorkoutOptionsResponseDto } from '../../../services/admin-workout.service';
import { MetActivity } from '../../../services/admin-met-activity.service';
import { fileService } from '../../../services/file.service';
import { getImageUrl } from '../../../utils/image-url.util';
import { MetActivityAutocomplete } from './MetActivityAutocomplete';

interface WorkoutDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  initialData?: any;
  options: WorkoutOptionsResponseDto;
  isLoading: boolean;
  title: string;
  submitLabel: string;
}

const locales: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

export const WorkoutDialog: React.FC<WorkoutDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  options,
  isLoading,
  title,
  submitLabel,
}) => {
  const [nameI18n, setNameI18n] = useState<Partial<Record<Locale, string>>>({});
  const [detailsI18n, setDetailsI18n] = useState<Partial<Record<Locale, string>>>({});
  const [category, setCategory] = useState<string>('');
  const [measurementUnit, setMeasurementUnit] = useState<string>('');
  const [metValue, setMetValue] = useState<number | string>('');
  const [metActivityId, setMetActivityId] = useState<number | null>(null);
  const [metActivity, setMetActivity] = useState<MetActivity | null>(null);
  const [defaultMeasurementValue, setDefaultMeasurementValue] = useState<number | ''>('');
  const [externalLink, setExternalLink] = useState<string>('');

  // Image
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageId, setImageId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Tabs for description
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) {
      if (initialData) {
        console.log('WorkoutDialog initialData:', initialData);
        setNameI18n(initialData.nameI18n || {});
        setDetailsI18n(initialData.detailsI18n || {});
        setCategory(initialData.category || '');
        // Use rawMeasurementUnit if available, otherwise fallback to MeasurementUnit.OTHER
        setMeasurementUnit(initialData.rawMeasurementUnit || MeasurementUnit.OTHER);
        setMetValue(initialData.metValue ?? '');
        setMetActivityId(initialData.metActivityId ?? null);
        setMetActivity(initialData.metActivity ?? null);
        setDefaultMeasurementValue(initialData.defaultMeasurementValue ?? '');
        setExternalLink(initialData.externalLink || '');

        // Image
        setImageId(initialData.imageId || null);
        setPreviewUrl(initialData.imageUrl ? getImageUrl(initialData.imageUrl) : null);
      } else {
        const initialNames: Partial<Record<Locale, string>> = {};
        const initialDetails: Partial<Record<Locale, string>> = {};
        locales.forEach(loc => { 
          initialNames[loc] = ''; 
          initialDetails[loc] = '';
        });
        setNameI18n(initialNames);
        setDetailsI18n(initialDetails);
        setCategory(options.categories.length > 0 ? options.categories[0].value : '');
        setMeasurementUnit(options.measurementUnits.length > 0 ? options.measurementUnits[0].value : '');
        setMetValue('');
        setMetActivityId(null);
        setMetActivity(null);
        setDefaultMeasurementValue('');
        setExternalLink('');

        setImageId(null);
        setPreviewUrl(null);
      }
      setSelectedFile(null);
      setActiveTab(0);
    }
  }, [open, initialData, options]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    let finalImageId = imageId;

    if (selectedFile) {
      setIsUploading(true);
      try {
        const uploadedFile = await fileService.uploadFile(selectedFile, 'Workout image');
        finalImageId = uploadedFile.id;
      } catch (error) {
        console.error('Failed to upload image:', error);
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    onSubmit({
      nameI18n,
      detailsI18n,
      category,
      measurementUnit,
      metValue: metValue === '' ? null : Number(metValue),
      metActivityId,
      defaultMeasurementValue: defaultMeasurementValue === '' ? null : Number(defaultMeasurementValue),
      externalLink: externalLink || null,
      imageId: finalImageId,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={3}>
            {/* Left Column: Translations & Image */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Зображення</Typography>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="workout-image-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="workout-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Завантажити фото
                  </Button>
                </label>
                
                {previewUrl && (
                  <Box sx={{ 
                    border: '1px dashed #ccc', 
                    borderRadius: 1, 
                    p: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: '#f5f5f5',
                    position: 'relative'
                  }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain' 
                      }}
                    />
                    <Button 
                      size="small" 
                      color="error" 
                      onClick={() => {
                        setPreviewUrl(null);
                        setSelectedFile(null);
                        setImageId(null);
                      }}
                      sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      Видалити
                    </Button>
                  </Box>
                )}
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Переклади назви</Typography>
              {locales.map(lang => (
                <TextField
                  key={lang}
                  margin="dense"
                  label={`Назва (${lang.toUpperCase()})`}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={nameI18n[lang] || ''}
                  onChange={e => setNameI18n({ ...nameI18n, [lang]: e.target.value })}
                  required={lang === 'uk'}
                  size="small"
                />
              ))}
            </Grid>

            {/* Right Column: Properties */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Властивості</Typography>
              <FormControl fullWidth margin="dense">
                <InputLabel>Категорія</InputLabel>
                <Select
                  value={category}
                  label="Категорія"
                  onChange={e => setCategory(e.target.value)}
                >
                  {options.categories.map(c => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                margin="dense"
                label="MET значення"
                type="number"
                fullWidth
                variant="outlined"
                value={metValue}
                onChange={e => setMetValue(e.target.value === '' ? '' : Number(e.target.value))}
                helperText="Наприклад: 4.0 (метаболічний еквівалент)"
              />
              
              {/* <TextField
                margin="dense"
                label="MET Activity ID"
                type="number"
                fullWidth
                variant="outlined"
                value={metActivityId === null ? '' : metActivityId}
                onChange={e => {
                  const val = e.target.value;
                  setMetActivityId(val === '' ? null : Number(val));
                  // Clear detail if ID changes manually
                  if (metActivity && metActivity.id !== Number(val)) {
                    setMetActivity(null); 
                  }
                }}
                helperText="FK на наукову бібліотеку MET (опціонально)"
              /> */}

              <Box sx={{ mt: 1, mb: 1 }}>
                <MetActivityAutocomplete 
                  value={metActivity}
                  onChange={(newValue) => {
                    setMetActivity(newValue);
                    setMetActivityId(newValue ? newValue.id : null);
                    if (newValue) {
                      setMetValue(newValue.metValue); // Auto-fill MET value
                    }
                  }}
                />
              </Box>

              {metActivity && (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 1, bgcolor: '#f0f7ff', borderColor: '#cfe8fc' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <ScienceIcon color="primary" fontSize="small" sx={{ mt: 0.3 }} />
                    <Box>
                      <Typography variant="subtitle2" component="div" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                        {metActivity.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {metActivity.nameEn}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label={`MET: ${metActivity.metValue}`} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {metActivity.categoryName} ({metActivity.compendiumType})
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              )}

              <TextField
                margin="dense"
                label="Дефолтна тривалість/кількість"
                type="number"
                fullWidth
                variant="outlined"
                value={defaultMeasurementValue}
                onChange={e => setDefaultMeasurementValue(e.target.value === '' ? '' : Number(e.target.value))}
                helperText="Значення за замовчуванням при додаванні"
              />
              <TextField
                margin="dense"
                label="Зовнішнє посилання"
                type="url"
                fullWidth
                value={externalLink}
                onChange={e => setExternalLink(e.target.value)}
                placeholder="https://..."
              />
            </Grid>

            {/* Description Section */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Опис / Інструкція</Typography>
              <Tabs 
                value={activeTab} 
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                {locales.map((lang) => (
                  <Tab key={lang} label={lang.toUpperCase()} />
                ))}
              </Tabs>
              {locales.map((lang, index) => (
                <div key={lang} role="tabpanel" hidden={activeTab !== index}>
                  {activeTab === index && (
                    <TextField
                      label={`Опис (${lang.toUpperCase()})`}
                      multiline
                      rows={4}
                      fullWidth
                      variant="outlined"
                      value={detailsI18n[lang] || ''}
                      onChange={e => setDetailsI18n({ ...detailsI18n, [lang]: e.target.value })}
                      placeholder="Введіть детальний опис, рецепт або інструкцію..."
                    />
                  )}
                </div>
              ))}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button onClick={handleSubmit} disabled={isLoading || isUploading} variant="contained">
          {isLoading || isUploading ? <CircularProgress size={24} /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
